import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { PersonStanding, FileText, Droplets, Headphones, Store, ShoppingCart, UserCheck, CircleHelp, MessageCircle, Bell, ChevronRight, Clock, X, LogOut, Users, Pill } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nutrifit-portal/artifacts/s4srivcq_Logo%20Icono%20Solo%20Nutriquimia%20SVG.svg";

const TAB_ITEMS = [
  { icon: PersonStanding, label: 'Mi Cuerpo', path: '/client/health-tests', color: '#467322' },
  { icon: Pill, label: 'Suplementos', path: '/client/supplements', color: '#F97316' },
  { icon: MessageCircle, label: 'Mensajes', path: '/client/messages', color: '#0D9488' },
  { icon: Users, label: 'Servicios', path: '/client/services', color: '#6366F1' },
  { icon: CircleHelp, label: 'Ayuda', path: '/client/help', color: '#F59E0B' },
];

const ClientLayout = () => {
  const { loading, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadChats, setUnreadChats] = useState(0);
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;
  const hideNavBar = location.pathname.startsWith('/client/prescriptions') 
    || location.pathname.startsWith('/client/analytics') 
    || location.pathname.includes('/health-tests/results')
    || location.pathname === '/client/health-tests-list'
    || location.pathname.startsWith('/client/health-survey')
    || (location.pathname.startsWith('/client/messages/') && location.pathname !== '/client/messages')
    || (location.pathname.startsWith('/client/services/'))
    || (location.pathname.startsWith('/client/supplements/'))
    || (location.pathname.startsWith('/client/help/'))
    || location.pathname === '/client/support'
    || location.pathname === '/client/contact';

  useEffect(() => {
    axios.get(`${API}/notifications`).then(res => {
      setNotifications(res.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const fetchUnreadChats = () => {
      axios.get(`${API}/messages/conversations`).then(res => {
        const count = res.data.filter(c => c.unread_count > 0).length;
        setUnreadChats(count);
      }).catch(() => {});
    };
    fetchUnreadChats();
    const interval = setInterval(fetchUnreadChats, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `hace ${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `hace ${hrs}h`;
    const days = Math.floor(hrs / 24);
    return `hace ${days}d`;
  };

  const isActive = (path) => {
    if (path === '/client/health-tests') {
      return location.pathname === '/client' || location.pathname.startsWith('/client/health-tests');
    }
    return location.pathname.startsWith(path);
  };

  const getActiveColor = () => {
    const active = TAB_ITEMS.find(item => isActive(item.path));
    return active?.color || '#10B981';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-emerald-100 border-t-emerald-500 animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-6 h-6 rounded-full bg-emerald-500/20" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col relative" data-testid="client-layout">
      {/* Subtle background decoration */}
      <div className="fixed top-0 right-0 w-72 h-72 bg-emerald-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none" />
      <div className="fixed bottom-32 left-0 w-56 h-56 bg-amber-500/5 rounded-full blur-3xl -translate-x-1/2 pointer-events-none" />

      {/* Top Bar - Glassmorphism */}
      <header className="bg-white/70 backdrop-blur-2xl border-b border-white/40 px-5 py-3 flex items-center justify-between sticky top-0 z-40 shadow-sm">
        {/* Left spacer for centering */}
        <div className="flex items-center gap-1 w-20">
        </div>

        {/* Centered Logo + Name */}
        <div className="flex items-center gap-2 absolute left-1/2 -translate-x-1/2">
          <img src={LOGO_URL} alt="Nutriquimia" className="w-8 h-8 object-contain" />
          <span className="font-bold text-sm tracking-tight" style={{ fontFamily: 'Barlow Condensed, sans-serif', color: '#467322' }}>
            Nutriquimia App
          </span>
        </div>

        <div className="flex items-center gap-1">
          {/* Notifications Bell */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="relative p-2.5 rounded-full hover:bg-gray-100/80 transition-all duration-200"
              data-testid="notifications-bell"
            >
              <Bell size={20} className="text-gray-500" />
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-4.5 h-4.5 bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>

            {showDropdown && (
              <div className="absolute right-0 top-12 w-80 bg-white/95 backdrop-blur-2xl rounded-3xl shadow-2xl border border-gray-100/50 overflow-hidden z-50 animate-scale-in" data-testid="notifications-dropdown">
                <div className="flex items-center justify-between px-5 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600">
                  <h3 className="font-bold text-white text-sm">Notificaciones</h3>
                  <button onClick={() => setShowDropdown(false)} className="p-1 rounded-full hover:bg-white/20 transition-colors">
                    <X size={16} className="text-white/80" />
                  </button>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="py-10 text-center">
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Bell size={20} className="text-gray-300" />
                      </div>
                      <p className="text-sm text-gray-400">Sin notificaciones</p>
                    </div>
                  ) : (
                    notifications.slice(0, 5).map((notif) => (
                      <div
                        key={notif.id}
                        className={`flex items-start gap-3 px-5 py-3.5 hover:bg-emerald-50/50 transition-colors cursor-pointer border-b border-gray-50 last:border-0 ${!notif.read ? 'bg-emerald-50/30' : ''}`}
                      >
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notif.read ? 'bg-emerald-500' : 'bg-transparent'}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{notif.title}</p>
                          <p className="text-xs text-gray-500 line-clamp-2 mt-0.5">{notif.message}</p>
                          <div className="flex items-center gap-1 mt-1.5">
                            <Clock size={10} className="text-gray-400" />
                            <span className="text-[10px] text-gray-400">{timeAgo(notif.created_at)}</span>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                {notifications.length > 0 && (
                  <button
                    onClick={() => { setShowDropdown(false); navigate('/client/notifications'); }}
                    className="w-full px-4 py-3 text-sm font-semibold text-emerald-600 hover:bg-emerald-50 transition-colors flex items-center justify-center gap-1 border-t border-gray-100"
                    data-testid="view-all-notifications"
                  >
                    Ver todas <ChevronRight size={14} />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Logout */}
          <button
            onClick={() => { logout(); navigate('/'); }}
            className="p-2.5 rounded-full hover:bg-gray-100/80 transition-all duration-200"
            data-testid="logout-btn"
          >
            <LogOut size={17} className="text-gray-400" />
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className={`flex-1 overflow-y-auto ${hideNavBar ? 'pb-6' : 'pb-28'} px-4 pt-5`}>
        <Outlet />
      </main>

      {/* Floating Island Bottom Tab Bar - hidden in sub-menus */}
      {!hideNavBar && (
      <nav className="fixed bottom-5 left-3 right-3 z-50 safe-area-bottom" data-testid="bottom-tab-bar">
        <div className="bg-white/90 backdrop-blur-2xl rounded-full shadow-[0_8px_40px_rgba(0,0,0,0.12)] border border-white/50 flex items-center justify-around max-w-lg mx-auto px-1 py-1.5 relative">
          {TAB_ITEMS.map((item) => {
            const IconComponent = item.icon;
            const active = isActive(item.path);
            const badge = item.label === 'Mensajes' && unreadChats > 0 ? unreadChats : 0;
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center rounded-full transition-all duration-300 relative ${
                  active 
                    ? '-mt-7 px-1' 
                    : 'px-1.5 py-1 hover:bg-gray-50'
                }`}
                data-testid={`tab-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {active ? (
                  <>
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center shadow-xl transition-all duration-300 relative"
                      style={{ 
                        background: `linear-gradient(135deg, ${item.color}, ${item.color}CC)`,
                        boxShadow: `0 6px 20px ${item.color}40`
                      }}
                    >
                      <IconComponent 
                        size={24} 
                        className="text-white transition-all duration-300"
                        strokeWidth={2.5}
                      />
                      {badge > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-rose-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 px-1" data-testid="messages-unread-badge">
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </div>
                    <span 
                      className="text-[9px] font-bold mt-0.5 leading-tight transition-colors duration-300"
                      style={{ color: item.color }}
                    >
                      {item.label}
                    </span>
                  </>
                ) : (
                  <>
                    <div className="p-1.5 rounded-full transition-all duration-300 relative">
                      <IconComponent 
                        size={19} 
                        className="text-gray-400 transition-colors duration-300"
                        strokeWidth={1.8}
                      />
                      {badge > 0 && (
                        <span className="absolute -top-0.5 -right-1 min-w-[16px] h-[16px] bg-rose-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-lg shadow-rose-500/30 px-0.5" data-testid="messages-unread-badge">
                          {badge > 9 ? '9+' : badge}
                        </span>
                      )}
                    </div>
                    <span className="text-[9px] font-semibold text-gray-400 leading-tight transition-colors duration-300">
                      {item.label}
                    </span>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </nav>
      )}
    </div>
  );
};

export default ClientLayout;
